import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { toast } from "sonner";
import { http } from "../../lib/api";
import { ArrowLeft, Loader2, ImagePlus, Save, Send, X, Sparkles } from "lucide-react";

const CATEGORIES = [
  "State of the Industry",
  "Permitting 101",
  "AHJ Spotlight",
  "Careers & Workforce",
  "Tech & AI in Construction",
];

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await http.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  // Store the RELATIVE path so images survive a domain change; resolves same-origin.
  return res.data.url;
}

export default function BlogEditor() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const quillRef = useRef(null);

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [cover, setCover] = useState(null);
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState("");
  const [author, setAuthor] = useState("Andres Campos");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [status, setStatus] = useState("draft");
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [genProvider, setGenProvider] = useState("gemini");
  const [genStyle, setGenStyle] = useState("auto");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const res = await http.get(`/admin/posts/${id}`);
        const p = res.data;
        setTitle(p.title || "");
        setSlug(p.slug || "");
        setSlugTouched(true);
        setExcerpt(p.excerpt || "");
        setContent(p.content || "");
        setCover(p.cover_image || null);
        setCategory(p.category || "");
        setTags((p.tags || []).join(", "));
        setAuthor(p.author || "Andres Campos");
        setMetaTitle(p.meta_title || "");
        setMetaDescription(p.meta_description || "");
        setStatus(p.status || "draft");
      } catch (e) {
        toast.error("Could not load post");
        navigate("/admin/blog");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, editing, navigate]);

  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const tid = toast.loading("Uploading image…");
      try {
        const url = await uploadImage(file);
        const quill = quillRef.current.getEditor();
        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, "image", url);
        quill.setSelection(range.index + 1);
        toast.success("Image added", { id: tid });
      } catch (e) {
        toast.error("Image upload failed", { id: tid });
      }
    };
  }, []);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [2, 3, false] }],
        ["bold", "italic", "underline", "blockquote"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image"],
        ["clean"],
      ],
      handlers: { image: imageHandler },
    },
  }), [imageHandler]);

  const onCoverSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const url = await uploadImage(file);
      setCover(url);
    } catch (err) {
      toast.error("Cover upload failed");
    } finally {
      setCoverUploading(false);
    }
  };

  const generateCover = async () => {
    setGenerating(true);
    const tid = toast.loading("Generating cover — this can take up to a minute…");
    try {
      const res = await http.post(
        "/blog/generate-cover",
        { provider: genProvider, style: genStyle, category: category || null, title: title.trim() || null },
        { timeout: 180000 }
      );
      setCover(res.data.url);
      toast.success("Cover generated — preview above", { id: tid });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed — try the other provider", { id: tid });
    } finally {
      setGenerating(false);
    }
  };

  const save = async (publish) => {
    if (!title.trim()) { toast.error("Add a title first"); return; }
    setSaving(true);
    const payload = {
      title: title.trim(),
      slug: slug.trim() || undefined,
      excerpt: excerpt.trim(),
      content,
      cover_image: cover,
      category: category || null,
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      author: author.trim() || "Andres Campos",
      meta_title: metaTitle.trim() || undefined,
      meta_description: metaDescription.trim() || undefined,
      status: publish ? "published" : status,
    };
    try {
      if (editing) await http.put(`/admin/posts/${id}`, payload);
      else await http.post("/admin/posts", payload);
      toast.success(publish ? "Published!" : "Saved");
      navigate("/admin/blog");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#EFEBDE]"><Loader2 size={30} className="animate-spin text-[#2C5F3A]" /></div>;
  }

  const inputCls = "w-full bg-[#FAF8F1] border border-[#16281C]/15 px-5 py-4 font-body text-[15px] text-[#16281C] placeholder:text-[#8B978C] focus:outline-none focus:border-[#2C5F3A] transition-colors";

  return (
    <div className="min-h-screen bg-[#EFEBDE] pt-28 pb-24">
      <div className="max-w-[900px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between gap-4 border-b border-[#16281C]/12 pb-6">
          <Link to="/admin/blog" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-[#66766A] hover:text-[#2C5F3A] transition-colors">
            <ArrowLeft size={14} /> Back
          </Link>
          <h1 className="font-display font-semibold text-2xl md:text-3xl tracking-tight text-[#16281C]">
            {editing ? "Edit Post" : "New Post"}
          </h1>
        </div>

        <div className="mt-8 space-y-6">
          <div>
            <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#66766A] block mb-2">Title *</label>
            <input
              data-testid="post-title-input"
              value={title}
              onChange={(e) => {
                const v = e.target.value;
                setTitle(v);
                if (!slugTouched) setSlug(v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, ""));
              }}
              className={`${inputCls} font-display text-xl`}
              placeholder="A strong, specific headline"
            />
          </div>

          <div>
            <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#66766A] block mb-2">Slug (URL) — auto from title, editable</label>
            <div className="flex items-center">
              <span className="font-mono text-[13px] text-[#8B978C] px-4 py-4 bg-[#EFEBDE] border border-r-0 border-[#16281C]/15">/blog/</span>
              <input
                data-testid="post-slug-input"
                value={slug}
                onChange={(e) => { setSlugTouched(true); setSlug(e.target.value); }}
                className={`${inputCls} border-l-0`}
                placeholder="my-post-url"
              />
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#66766A] block mb-2">Excerpt</label>
            <textarea data-testid="post-excerpt-input" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} rows={2} className={`${inputCls} resize-none`} placeholder="One or two sentences shown on cards and previews." />
          </div>

          <div>
            <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#66766A] block mb-2">Cover image</label>
            {cover ? (
              <div className="relative w-full aspect-[16/9] bg-[#DDE4D5] border border-[#16281C]/12 overflow-hidden">
                <img src={cover} alt="cover" className="w-full h-full object-cover" />
                <button onClick={() => setCover(null)} className="absolute top-3 right-3 bg-[#16281C]/80 text-[#EFEBDE] p-2 hover:bg-[#16281C]" title="Remove"><X size={16} /></button>
              </div>
            ) : (
              <label data-testid="cover-upload" className="flex items-center justify-center gap-3 w-full aspect-[16/9] border border-dashed border-[#16281C]/25 bg-[#FAF8F1] cursor-pointer hover:border-[#2C5F3A] transition-colors text-[#66766A]">
                {coverUploading ? <Loader2 size={22} className="animate-spin text-[#2C5F3A]" /> : <><ImagePlus size={22} /> <span className="font-mono text-[11px] tracking-[0.15em] uppercase">Upload cover</span></>}
                <input type="file" accept="image/*" className="hidden" onChange={onCoverSelect} />
              </label>
            )}
            <div className="mt-3 border border-[#16281C]/12 bg-[#FAF8F1] p-5" data-testid="cover-generator">
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#2C5F3A]">Or generate an on-brand cover with AI</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#66766A] block mb-2">Image service</label>
                  <select data-testid="gen-provider-select" value={genProvider} onChange={(e) => setGenProvider(e.target.value)} className={`${inputCls} appearance-none cursor-pointer`}>
                    <option value="gemini">Gemini — Nano Banana</option>
                    <option value="openai">OpenAI — GPT Image 1</option>
                  </select>
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#66766A] block mb-2">Style</label>
                  <select data-testid="gen-style-select" value={genStyle} onChange={(e) => setGenStyle(e.target.value)} className={`${inputCls} appearance-none cursor-pointer`}>
                    <option value="auto">Match category (auto)</option>
                    <option value="redwoods">Redwoods — forest &amp; sage</option>
                    <option value="editorial">Clean editorial — geometric</option>
                  </select>
                </div>
              </div>
              <button
                type="button"
                onClick={generateCover}
                disabled={generating}
                data-testid="generate-cover-button"
                className="mt-4 inline-flex items-center gap-2 border border-[#2C5F3A]/40 text-[#2C5F3A] font-mono text-[11px] tracking-[0.2em] uppercase px-6 py-3.5 hover:bg-[#2C5F3A] hover:text-[#EFEBDE] transition-colors disabled:opacity-60"
              >
                {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {generating ? "Generating…" : "Generate cover"}
              </button>
              <p className="mt-3 font-body text-xs text-[#8B978C]">
                Uses your title &amp; category for direction. Takes up to a minute; the result becomes the cover above and can be removed or replaced.
              </p>
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#66766A] block mb-2">Content</label>
            <div className="bg-[#FAF8F1] border border-[#16281C]/15">
              <ReactQuill ref={quillRef} theme="snow" value={content} onChange={setContent} modules={modules} placeholder="Write your post…" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#66766A] block mb-2">Category</label>
              <select data-testid="post-category-select" value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputCls} appearance-none cursor-pointer`}>
                <option value="">Select category…</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#66766A] block mb-2">Author</label>
              <input data-testid="post-author-input" value={author} onChange={(e) => setAuthor(e.target.value)} className={inputCls} placeholder="Andres Campos" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#66766A] block mb-2">Tags (comma separated)</label>
              <input data-testid="post-tags-input" value={tags} onChange={(e) => setTags(e.target.value)} className={inputCls} placeholder="Permitting, Pre-Development" />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#66766A] block mb-2">Status</label>
              <select data-testid="post-status-select" value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} appearance-none cursor-pointer`}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>

          <div className="border-t border-[#16281C]/12 pt-6">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#2C5F3A] mb-4">SEO &amp; Sharing</p>
            <div className="space-y-4">
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#66766A] block mb-2">Meta title <span className="text-[#8B978C]">(defaults to title)</span></label>
                <input data-testid="post-metatitle-input" value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} className={inputCls} placeholder="Shown in search results & browser tab" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#66766A] block mb-2">Meta description <span className="text-[#8B978C]">(~155 chars, for search & LinkedIn preview)</span></label>
                <textarea data-testid="post-metadesc-input" value={metaDescription} onChange={(e) => setMetaDescription(e.target.value)} rows={2} maxLength={200} className={`${inputCls} resize-none`} placeholder="A compelling one-sentence summary." />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 pt-4">
            <button onClick={() => save(false)} disabled={saving} data-testid="save-draft-button" className="inline-flex items-center gap-2 border border-[#16281C]/25 text-[#16281C] font-mono text-xs tracking-[0.2em] uppercase px-7 py-4 hover:border-[#2C5F3A] hover:text-[#2C5F3A] transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
            </button>
            <button onClick={() => save(true)} disabled={saving} data-testid="publish-button" className="inline-flex items-center gap-2 bg-[#2C5F3A] text-[#EFEBDE] font-mono text-xs tracking-[0.2em] uppercase px-7 py-4 hover:bg-[#367A49] transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Publish
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
